import React, { createContext, useContext, useState, useRef, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import DocumentService from '../Services/documents.service';

const OcrJobsContext = createContext(undefined);

const POLL_INTERVAL_MS = 10000;

let groupCounter = 0;

const normaliseStatus = (raw) => {
    if (raw === 2 || raw === 'Completed') return 'Completed';
    if (raw === 3 || raw === 'Failed') return 'Failed';
    return 'Pending';
};

// Older API versions omit these; dropping undefined keeps locally known values when merging.
const extractJobDetails = (s) => Object.fromEntries(Object.entries({
    createdAt: s.CreatedAt ?? s.createdAt,
    createdAtIsrael: s.CreatedAtIsrael ?? s.createdAtIsrael,
    updatedAt: s.UpdatedAt ?? s.updatedAt,
    updatedAtIsrael: s.UpdatedAtIsrael ?? s.updatedAtIsrael,
    entityType: s.EntityType ?? s.entityType,
    attempt: s.Attempt ?? s.attempt,
    requeueCount: s.RequeueCount ?? s.requeueCount,
    fileNames: s.FileNames ?? s.fileNames,
    ageMinutes: s.AgeMinutes ?? s.ageMinutes,
    isStuck: s.IsStuck ?? s.isStuck,
    errorMessage: s.ErrorMessage ?? s.errorMessage,
}).filter(([, v]) => v !== undefined));

const extractJobFields = (s) => {
    if (!s) return null;
    const status = normaliseStatus(s.Status ?? s.status);
    return {
        status,
        statusLabel: s.StatusLabel ?? s.statusLabel ?? null,
        estimatedSeconds: s.EstimatedSecondsRemaining ?? s.estimatedSecondsRemaining ?? null,
        progressPercent: s.ProgressPercent ?? s.progressPercent ?? 0,
        filesProcessed: s.FilesProcessedEstimate ?? s.filesProcessedEstimate ?? 0,
        fileCount: s.FileCount ?? s.fileCount ?? undefined,
        ...extractJobDetails(s),
    };
};

const mapEntityTypeToJobType = (entityType) => {
    if (!entityType) return 'ocr';
    const lower = entityType.toLowerCase();
    if (lower.includes('payment') || lower.includes('pa')) return 'pa-ocr';
    return 'ocr';
};

export const OcrJobsProvider = ({ children, token, caseId, accountantId }) => {
    const { t } = useTranslation();
    const [jobs, setJobs] = useState([]);
    const jobsRef = useRef(jobs);
    const callbacksRef = useRef({});
    const tokenRef = useRef(token);
    const caseIdRef = useRef(caseId);
    const pollInFlightRef = useRef(false);
    const intervalRef = useRef(null);
    const lastFetchKeyRef = useRef(null);

    useEffect(() => { tokenRef.current = token; }, [token]);
    useEffect(() => { jobsRef.current = jobs; }, [jobs]);
    useEffect(() => { caseIdRef.current = caseId; }, [caseId]);

    const fetchKey = caseId ? `case:${caseId}` : accountantId ? `acc:${accountantId}` : null;

    useEffect(() => {
        if (!token || !fetchKey || lastFetchKeyRef.current === fetchKey) return;
        lastFetchKeyRef.current = fetchKey;

        (async () => {
            try {
                const activeJobs = caseId
                    ? await DocumentService.GetActiveJobsByCaseId(caseId, token)
                    : await DocumentService.GetActiveJobsByAccountantId(accountantId, token);

                if (!Array.isArray(activeJobs) || activeJobs.length === 0) return;

                const restoredGroupId = `restored_${fetchKey}_${Date.now()}`;

                setJobs(prev => {
                    const existingIds = new Set(prev.map(j => j.id));
                    const newJobs = activeJobs
                        .filter(j => !existingIds.has(j.Id ?? j.id))
                        .map(j => ({
                            id: j.Id ?? j.id,
                            type: mapEntityTypeToJobType(j.EntityType ?? j.entityType),
                            groupId: restoredGroupId,
                            status: normaliseStatus(j.Status ?? j.status),
                            statusLabel: j.StatusLabel ?? j.statusLabel ?? null,
                            estimatedSeconds: j.EstimatedSecondsRemaining ?? j.estimatedSecondsRemaining ?? null,
                            progressPercent: j.ProgressPercent ?? j.progressPercent ?? 0,
                            filesProcessed: j.FilesProcessedEstimate ?? j.filesProcessedEstimate ?? 0,
                            fileCount: j.FileCount ?? j.fileCount ?? 0,
                            caseId: j.CaseId ?? j.caseId ?? caseId ?? '',
                            clientName: j.ClientName ?? j.clientName ?? '',
                            reportingMonth: j.ReportingMonth ?? j.reportingMonth ?? '',
                            ...extractJobDetails(j),
                        }));

                    if (newJobs.length === 0) return prev;
                    return [...prev, ...newJobs];
                });
            } catch (err) {
                console.error('Failed to fetch active OCR jobs from backend:', err);
            }
        })();
    }, [fetchKey, token, caseId, accountantId]);

    const addJobs = useCallback((jobIds, type, onComplete, metadata = {}) => {
        if (!jobIds || jobIds.length === 0) return;

        groupCounter += 1;
        const groupId = `group_${groupCounter}_${Date.now()}`;

        if (onComplete) {
            callbacksRef.current[groupId] = { cb: onComplete, caseId: metadata.caseId || '' };
        }

        const newJobs = jobIds.map(id => ({
            id,
            type,
            groupId,
            status: 'Pending',
            statusLabel: 'Queued',
            estimatedSeconds: null,
            fileCount: metadata.fileCount || 0,
            caseId: metadata.caseId || '',
            clientName: metadata.clientName || '',
            reportingMonth: metadata.reportingMonth || '',
            createdAt: new Date().toISOString(),
        }));

        setJobs(prev => [...prev, ...newJobs]);
    }, []);

    const clearCompletedJobs = useCallback(() => {
        setJobs(prev => prev.filter(j => j.status === 'Pending'));
    }, []);

    /**
     * Cancel a stuck or in-flight OCR job.
     *  - Calls the matching backend endpoint based on job.type ('pa-ocr' vs 'ocr').
     *  - On success (200 OK or 409 already-completed/already-failed) updates the
     *    local job to 'Failed' so the panel reflects reality immediately.
     *  - On 404 we still drop the job from local state (it doesn't exist server-side).
     *  - Returns the parsed server result so the caller can toast a per-outcome message.
     */
    const cancelJob = useCallback(async (jobId) => {
        const currentToken = tokenRef.current;
        if (!currentToken || !jobId) {
            return { ok: false, status: 0, outcome: 'NoToken' };
        }

        const target = jobsRef.current.find(j => j.id === jobId);
        if (!target) {
            return { ok: false, status: 0, outcome: 'NotInList' };
        }

        try {
            const response = target.type === 'pa-ocr'
                ? await DocumentService.CancelAsyncPaOcrJob(jobId, currentToken)
                : await DocumentService.CancelAsyncOcrJob(jobId, currentToken);

            const outcome = response?.body?.outcome ?? null;
            const errorMessage = response?.body?.message ?? response?.body?.title ?? null;

            // Treat both 2xx and 404 as "remove from local list".
            // 409 (AlreadyCompleted) we keep so the user can see the completed state.
            if (response.ok || response.status === 404) {
                setJobs(prev => prev.map(j =>
                    j.id === jobId
                        ? { ...j, status: 'Failed', statusLabel: 'Cancelled', errorMessage: errorMessage || 'Cancelled by user' }
                        : j));
            }

            return { ok: response.ok, status: response.status, outcome, body: response.body };
        } catch (err) {
            console.error('Failed to cancel OCR job:', err);
            return { ok: false, status: 0, outcome: 'NetworkError', error: err };
        }
    }, []);

    const fireGroupCallbacks = useCallback((completedGroupIds, currentJobs) => {
        completedGroupIds.forEach(gid => {
            const groupJobs = currentJobs.filter(j => j.groupId === gid);
            const failedCount = groupJobs.filter(j => j.status === 'Failed').length;
            const completedCount = groupJobs.filter(j => j.status === 'Completed').length;

            if (failedCount === 0) {
                toast.success(t('documentsProcessingComplete'));
            } else if (completedCount === 0) {
                toast.error(t('documentsProcessingAllFailed'));
            } else {
                toast.warning(t('documentsProcessingPartialFail', { failed: failedCount, total: groupJobs.length }));
            }

            const entry = callbacksRef.current[gid];
            if (entry?.cb) {
                const jobCaseId = entry.caseId || groupJobs[0]?.caseId;
                if (jobCaseId && jobCaseId === caseIdRef.current) {
                    try { entry.cb(); } catch (err) { console.error('OCR job onComplete error:', err); }
                } else {
                    toast.info(t('ocrJobsRefreshNeeded'), { autoClose: 8000 });
                }
                delete callbacksRef.current[gid];
            } else if (completedCount > 0) {
                toast.info(t('ocrJobsRefreshNeeded'), { autoClose: 8000 });
            }
        });
    }, [t]);

    const hasPending = jobs.some(j => j.status === 'Pending');

    useEffect(() => {
        if (!hasPending) {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
            return;
        }

        if (intervalRef.current) return;

        intervalRef.current = setInterval(async () => {
            if (pollInFlightRef.current) return;
            pollInFlightRef.current = true;

            try {
                const currentToken = tokenRef.current;
                if (!currentToken) return;

                const currentJobs = jobsRef.current;
                const pendingJobs = currentJobs.filter(j => j.status === 'Pending');
                if (pendingJobs.length === 0) return;

                const statusResults = await Promise.all(
                    pendingJobs.map(job => {
                        if (job.type === 'pa-ocr') {
                            return DocumentService.GetAsyncPaOcrJobStatus(job.id, currentToken).catch(() => null);
                        }
                        return DocumentService.GetAsyncOcrJobStatus(job.id, currentToken).catch(() => null);
                    })
                );

                const updatedMap = {};
                pendingJobs.forEach((job, i) => {
                    const fields = extractJobFields(statusResults[i]);
                    if (fields) {
                        updatedMap[job.id] = fields;
                    }
                });

                if (Object.keys(updatedMap).length === 0) return;

                setJobs(prev => {
                    const next = prev.map(j => {
                        const update = updatedMap[j.id];
                        if (update) {
                            return { ...j, ...update };
                        }
                        return j;
                    });

                    const groupIds = [...new Set(pendingJobs.map(j => j.groupId))];
                    const completedGroups = groupIds.filter(gid => {
                        const groupJobs = next.filter(j => j.groupId === gid);
                        return groupJobs.every(j => j.status === 'Completed' || j.status === 'Failed');
                    });

                    if (completedGroups.length > 0) {
                        setTimeout(() => fireGroupCallbacks(completedGroups, next), 0);
                    }

                    return next;
                });

                const allNowDone = Object.values(updatedMap).length === pendingJobs.length &&
                    Object.values(updatedMap).every(f => f.status === 'Completed' || f.status === 'Failed');

                if (allNowDone) {
                    clearInterval(intervalRef.current);
                    intervalRef.current = null;
                }
            } catch (err) {
                console.error('OCR job polling error:', err);
            } finally {
                pollInFlightRef.current = false;
            }
        }, POLL_INTERVAL_MS);

        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
        };
    }, [hasPending, fireGroupCallbacks, t]);

    const value = { jobs, addJobs, clearCompletedJobs, cancelJob };

    return (
        <OcrJobsContext.Provider value={value}>
            {children}
        </OcrJobsContext.Provider>
    );
};

export const useOcrJobs = () => {
    const ctx = useContext(OcrJobsContext);
    if (ctx === undefined) {
        throw new Error('useOcrJobs must be used within an OcrJobsProvider');
    }
    return ctx;
};
